<script lang="ts">
  import { onMount } from 'svelte';
  import { editorStore, currentData } from '../../stores/editorStore';
  import { undoStore } from '../../stores/undoStore';
  import { announceToScreenReader } from '../../lib/accessibility';
  import type { PromptFile, Prompt, Category } from '@blather/shared';

  let overview: any = null;
  let selectedCategory: Category | null = null;
  let newPrompt: Partial<Prompt> = {
    word: '',
    explanation: '',
    aliases: [],
  };
  let aliasesInput = '';

  onMount(async () => {
    try {
      overview = await editorStore.loadOverview();
      await editorStore.setMode('prompts', overview);
    } catch (error) {
      console.error('Failed to load overview:', error);
    }
  });

  $: promptFile = $currentData as PromptFile | null;
  $: prompts = promptFile?.prompts || [];

  async function selectCategory(category: Category) {
    selectedCategory = category;
    try {
      await editorStore.selectItem(category);
      announceToScreenReader(`Loaded ${category} prompts with ${prompts.length} items`, 'polite');
    } catch (error) {
      console.error('Failed to load prompts:', error);
    }
  }

  function addPrompt() {
    if (!selectedCategory || !promptFile || !newPrompt.word || !newPrompt.explanation) return;

    const aliases = aliasesInput
      .split(',')
      .map(a => a.trim())
      .filter(a => a.length > 0);

    const prompt: Prompt = {
      word: newPrompt.word.trim(),
      explanation: newPrompt.explanation.trim(),
      aliases,
    };

    // Check for duplicate word
    if (prompts.some(p => p.word.toLowerCase() === prompt.word.toLowerCase())) {
      announceToScreenReader(`Prompt "${prompt.word}" already exists`, 'assertive');
      return;
    }

    const currentFile = promptFile;
    const updatedFile: PromptFile = {
      ...currentFile,
      prompts: [...currentFile.prompts, prompt],
    };

    undoStore.recordAction({
      type: 'add',
      target: 'prompt',
      targetKey: selectedCategory,
      itemKey: prompt.word,
      previousValue: currentFile,
      newValue: updatedFile,
    });

    editorStore.updateLocalData('prompts', selectedCategory, updatedFile);

    // Reset form
    newPrompt = { word: '', explanation: '', aliases: [] };
    aliasesInput = '';
    announceToScreenReader(`Added prompt: ${prompt.word}`, 'polite');
  }

  function deletePrompt(index: number) {
    if (!selectedCategory || !promptFile) return;

    const promptToDelete = prompts[index];
    const currentFile = promptFile;
    const updatedFile: PromptFile = {
      ...currentFile,
      prompts: currentFile.prompts.filter((_, i) => i !== index),
    };

    undoStore.recordAction({
      type: 'delete',
      target: 'prompt',
      targetKey: selectedCategory,
      itemKey: promptToDelete.word,
      previousValue: currentFile,
      newValue: updatedFile,
    });

    editorStore.updateLocalData('prompts', selectedCategory, updatedFile);
    announceToScreenReader(`Deleted prompt: ${promptToDelete.word}`, 'polite');
  }
</script>

<div class="editor-container">
  <section aria-labelledby="category-select-heading">
    <h2 id="category-select-heading">Select Category</h2>

    {#if $editorStore.isLoading && !overview}
      <p>Loading categories...</p>
    {:else if overview}
      <select
        bind:value={selectedCategory}
        on:change={() => selectedCategory && selectCategory(selectedCategory)}
        aria-label="Choose category to edit"
        class="category-select"
      >
        <option value={null}>-- Select a category --</option>
        {#each overview.promptCategories as cat}
          <option value={cat}>{cat}</option>
        {/each}
      </select>
    {:else}
      <p class="error-text">Failed to load categories</p>
    {/if}
  </section>

  {#if selectedCategory && promptFile}
    <section aria-labelledby="prompts-heading">
      <h2 id="prompts-heading">Prompts ({prompts.length})</h2>

      {#if prompts.length === 0}
        <p class="empty-message">No prompts in this category. Add one below!</p>
      {:else}
        <div class="table-container">
          <table>
            <caption class="visually-hidden">Prompts for {selectedCategory}</caption>
            <thead>
              <tr>
                <th scope="col">Word</th>
                <th scope="col">Explanation</th>
                <th scope="col">Aliases</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each prompts as prompt, i}
                <tr>
                  <td class="word-cell">{prompt.word}</td>
                  <td class="explanation-cell">{prompt.explanation}</td>
                  <td class="aliases-cell">
                    {#if prompt.aliases && prompt.aliases.length > 0}
                      <ul aria-label="Aliases for {prompt.word}">
                        {#each prompt.aliases as alias}
                          <li>{alias}</li>
                        {/each}
                      </ul>
                    {:else}
                      <span class="no-aliases">None</span>
                    {/if}
                  </td>
                  <td class="actions-cell">
                    <button
                      on:click={() => deletePrompt(i)}
                      aria-label="Delete prompt: {prompt.word}"
                      class="delete-button"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}

      <div class="add-prompt-section">
        <h3>Add New Prompt</h3>

        <div class="form-group">
          <label for="p-word">Word <span class="required">*</span></label>
          <input
            id="p-word"
            type="text"
            bind:value={newPrompt.word}
            placeholder="e.g., coffee"
            aria-required="true"
          />
        </div>

        <div class="form-group">
          <label for="p-explanation">Explanation <span class="required">*</span></label>
          <textarea
            id="p-explanation"
            bind:value={newPrompt.explanation}
            placeholder="e.g., A hot beverage made from roasted beans"
            rows="3"
            aria-required="true"
          />
        </div>

        <div class="form-group">
          <label for="p-aliases">Aliases (comma-separated)</label>
          <input
            id="p-aliases"
            type="text"
            bind:value={aliasesInput}
            placeholder="e.g., java, joe, cuppa"
            aria-describedby="aliases-hint"
          />
          <p id="aliases-hint" class="hint">Enter alternative acceptable answers, separated by commas</p>
        </div>

        <button
          on:click={addPrompt}
          disabled={!newPrompt.word || !newPrompt.explanation}
          aria-disabled={!newPrompt.word || !newPrompt.explanation}
          class="add-button"
        >
          Add Prompt
        </button>
      </div>
    </section>
  {/if}
</div>

<style>
  .editor-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
  }

  section {
    margin-bottom: 2rem;
  }

  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--color-text-primary, #333);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.125rem;
    color: var(--color-text-primary, #333);
  }

  .category-select {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
    background: white;
  }

  .category-select:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .table-container {
    overflow-x: auto;
    margin-bottom: 2rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    background: white;
  }

  thead {
    background: var(--color-bg-secondary, #f8f9fa);
  }

  th {
    padding: 0.75rem;
    text-align: left;
    font-weight: 600;
    color: var(--color-text-primary, #333);
    border-bottom: 2px solid var(--color-border, #ddd);
  }

  td {
    padding: 0.75rem;
    border-bottom: 1px solid var(--color-border-light, #eee);
  }

  tr:hover {
    background: var(--color-bg-hover, #f8f9fa);
  }

  .word-cell {
    font-weight: 500;
    color: var(--color-primary, #007bff);
  }

  .explanation-cell {
    max-width: 300px;
    line-height: 1.4;
  }

  .aliases-cell ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .aliases-cell li {
    padding: 0.25rem 0.5rem;
    background: var(--color-bg-secondary, #e9ecef);
    border-radius: 4px;
    font-size: 0.875rem;
  }

  .no-aliases {
    color: var(--color-text-secondary, #999);
    font-style: italic;
    font-size: 0.875rem;
  }

  .actions-cell {
    text-align: center;
  }

  .delete-button {
    background: var(--color-danger, #dc3545);
    color: white;
    border: none;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .delete-button:hover {
    background: var(--color-danger-hover, #c82333);
  }

  .delete-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .add-prompt-section {
    background: var(--color-bg-secondary, #f8f9fa);
    padding: 1.5rem;
    border-radius: 8px;
    border: 2px dashed var(--color-border, #ddd);
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--color-text-primary, #333);
  }

  .required {
    color: var(--color-danger, #dc3545);
  }

  input[type="text"],
  textarea {
    width: 100%;
    padding: 0.75rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
    font-family: inherit;
  }

  textarea {
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .hint {
    margin: 0.5rem 0 0 0;
    font-size: 0.875rem;
    color: var(--color-text-secondary, #666);
  }

  .add-button {
    background: var(--color-success, #28a745);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .add-button:hover:not(:disabled) {
    background: var(--color-success-hover, #218838);
  }

  .add-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .add-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .empty-message {
    padding: 2rem;
    text-align: center;
    color: var(--color-text-secondary, #666);
    background: var(--color-bg-secondary, #f8f9fa);
    border-radius: 8px;
  }

  .error-text {
    color: var(--color-danger, #dc3545);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
